import {
  MAX_BUCKET_NAME_LENGTH,
  SUPER_COPY_REVIEW_CONFIDENCE_THRESHOLD,
  type SuperCopyOperationStatus,
  type SuperCopyReviewClient,
  type SuperCopyReviewGroup,
  type SuperCopyReviewValue
} from '@oysterjs/smartfill-contracts';
import {
  newGroup,
  newValue,
  removeGroup,
  reviewedState,
  semanticKeyForLabel
} from '../sidebar/superCopyDraft';
import { truncateChars } from '../sidebar/presentation';
import { PlusIcon, TrashIcon } from './Icons';
import { InlineError } from './SidebarState';

export function ClientReview({
  client,
  index,
  operation,
  disabled,
  invalid,
  conflicts,
  onChange,
  onOpenBucket
}: {
  client: SuperCopyReviewClient;
  index: number;
  operation?: SuperCopyOperationStatus;
  disabled: boolean;
  invalid: Set<string>;
  conflicts: Set<string>;
  onChange: (client: SuperCopyReviewClient) => void;
  /** Opens the bucket a failed operation nonetheless created. */
  onOpenBucket?: (operation: SuperCopyOperationStatus) => void;
}) {
  const savedDespiteFailure =
    operation !== undefined && operation.state === 'FAILED' && operation.bucketId !== null;
  return (
    <article className="super-copy-client">
      <div className="super-copy-client__heading">
        <span>Client {index + 1}</span>
        {operation && <OperationPill operation={operation} />}
      </div>
      <label className="super-copy-name">
        <span>Bucket name</span>
        <input
          aria-label={`Client ${index + 1} bucket name`}
          value={client.name}
          disabled={disabled}
          aria-invalid={invalid.has(`client:${client.id}`)}
          onChange={(event) =>
            onChange({
              ...client,
              name: truncateChars(event.target.value, MAX_BUCKET_NAME_LENGTH)
            })
          }
        />
      </label>

      {client.groups.map((group) => (
        <GroupReview
          key={group.id}
          clientId={client.id}
          group={group}
          ownsRecords={client.groups.some(
            (candidate) => candidate.entity?.parentGroupId === group.id
          )}
          parentLabel={
            client.groups.find((candidate) => candidate.id === group.entity?.parentGroupId)?.label
          }
          disabled={disabled}
          invalid={invalid}
          conflicts={conflicts}
          onChange={(next) =>
            onChange({
              ...client,
              groups: client.groups.map((candidate) =>
                candidate.id === group.id ? next : candidate
              )
            })
          }
          onRemove={() => onChange(removeGroup(client, group.id))}
        />
      ))}

      <button
        type="button"
        className="super-copy-add"
        disabled={disabled}
        onClick={() =>
          onChange({
            ...client,
            groups: [...client.groups, newGroup()]
          })
        }
      >
        <PlusIcon />
        Add group
      </button>
      {operation?.error && <InlineError message={operation.error.message} />}
      {savedDespiteFailure && onOpenBucket && (
        <button
          type="button"
          className="button button--secondary super-copy-bucket-link"
          onClick={() => onOpenBucket(operation)}
        >
          Open {operation.bucketName ?? 'saved bucket'}
        </button>
      )}
    </article>
  );
}

function GroupReview({
  clientId,
  group,
  ownsRecords,
  parentLabel,
  disabled,
  invalid,
  conflicts,
  onChange,
  onRemove
}: {
  clientId: string;
  group: SuperCopyReviewGroup;
  ownsRecords: boolean;
  parentLabel?: string;
  disabled: boolean;
  invalid: Set<string>;
  conflicts: Set<string>;
  onChange: (group: SuperCopyReviewGroup) => void;
  onRemove: () => void;
}) {
  return (
    <section className="super-copy-group">
      <div className="super-copy-group__heading">
        <input
          aria-label={`${group.label || 'Group'} name`}
          value={group.label}
          disabled={disabled}
          aria-invalid={invalid.has(`group:${clientId}:${group.id}`)}
          onChange={(event) => onChange({ ...group, label: event.target.value })}
        />
        <button
          type="button"
          className="icon-button icon-button--plain"
          aria-label={`Remove ${group.label || 'group'}${ownsRecords ? ' and its owned records' : ''}`}
          title={ownsRecords ? 'Remove this record and its owned records' : undefined}
          disabled={disabled}
          onClick={onRemove}
        >
          <TrashIcon />
        </button>
      </div>

      {group.entity && (
        <p className="editor-hint">
          {group.entity.type}
          {group.entity.role ? ` · ${group.entity.role}` : ''}
          {parentLabel ? ` · belongs to ${parentLabel}` : ''}
        </p>
      )}

      {group.values.map((value) => {
        const marker = `value:${clientId}:${group.id}:${value.id}`;
        return (
          <ValueReview
            key={value.id}
            value={value}
            disabled={disabled}
            invalid={invalid.has(marker)}
            conflict={conflicts.has(marker)}
            onChange={(next) =>
              onChange({
                ...group,
                values: group.values.map((candidate) =>
                  candidate.id === value.id ? next : candidate
                )
              })
            }
            onRemove={() =>
              onChange({
                ...group,
                values: group.values.filter((candidate) => candidate.id !== value.id)
              })
            }
          />
        );
      })}

      <button
        type="button"
        className="super-copy-add super-copy-add--field"
        disabled={disabled}
        onClick={() => onChange({ ...group, values: [...group.values, newValue()] })}
      >
        <PlusIcon />
        Add field
      </button>
    </section>
  );
}

function ValueReview({
  value,
  disabled,
  invalid,
  conflict,
  onChange,
  onRemove
}: {
  value: SuperCopyReviewValue;
  disabled: boolean;
  invalid: boolean;
  conflict: boolean;
  onChange: (value: SuperCopyReviewValue) => void;
  onRemove: () => void;
}) {
  const needsReview = value.confidence < SUPER_COPY_REVIEW_CONFIDENCE_THRESHOLD && !value.accepted;
  return (
    <div
      className={[
        'super-copy-value',
        needsReview ? 'needs-review' : '',
        invalid || conflict ? 'has-error' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="super-copy-value__top">
        <label>
          <span>Field name</span>
          <input
            aria-label={`${value.label} field name`}
            value={value.label}
            disabled={disabled}
            aria-invalid={invalid || conflict}
            onChange={(event) => {
              const label = event.target.value;
              // Extracted keys are provenance and survive label edits; values
              // Cara could not classify take their identity from the label.
              const semanticKey = semanticKeyForLabel(value.semanticKey, label);
              onChange({
                ...value,
                label,
                semanticKey,
                accepted: true,
                reviewState: reviewedState(semanticKey, true)
              });
            }}
          />
        </label>
        <button
          type="button"
          className="icon-button icon-button--plain"
          aria-label={`Remove ${value.label}`}
          disabled={disabled}
          onClick={onRemove}
        >
          <TrashIcon />
        </button>
      </div>
      <label>
        <span>Value</span>
        <textarea
          aria-label={`${value.label} value`}
          value={value.value}
          disabled={disabled}
          aria-invalid={invalid}
          onChange={(event) =>
            onChange({
              ...value,
              value: event.target.value,
              accepted: true,
              reviewState: reviewedState(value.semanticKey, true)
            })
          }
        />
      </label>
      {needsReview && (
        <button
          type="button"
          className="super-copy-review-button"
          disabled={disabled}
          onClick={() =>
            onChange({
              ...value,
              accepted: true,
              reviewState: reviewedState(value.semanticKey, false)
            })
          }
        >
          Review and accept
        </button>
      )}
      {conflict && (
        <small className="field-error">This field name is used twice in one group.</small>
      )}
      {!conflict && invalid && (
        <small className="field-error">Give this field a name and a value.</small>
      )}
    </div>
  );
}

export function OperationPill({ operation }: { operation: SuperCopyOperationStatus }) {
  return (
    <span className={`operation-pill operation-pill--${operation.state.toLowerCase()}`}>
      {operationLabels[operation.state]}
    </span>
  );
}

const operationLabels: Record<SuperCopyOperationStatus['state'], string> = {
  READY: 'Ready',
  SAVING: 'Saving',
  BUCKET_SAVED: 'Bucket saved',
  EVIDENCE_PENDING: 'Evidence pending',
  COMPLETE: 'Complete',
  FAILED: 'Retry needed'
};
